import { X } from 'lucide-react';

type AdDataDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  companyName: string;
  source: 'google' | 'facebook' | 'linkedin';
  data: any;
};

const esc = (str: string | null | undefined): string => {
  if (!str) return '';
  return String(str).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] || c));
};

const pick = (item: any, keys: string[]) => {
  for (const key of keys) {
    const val = item?.[key];
    if (val != null && String(val).trim() !== '') return val;
  }
  return '';
};

const pickNested = (item: any, paths: string[]) => {
  for (const path of paths) {
    const parts = path.split('.');
    let cur = item;
    for (const part of parts) {
      cur = cur?.[part];
    }
    if (cur != null && String(cur).trim() !== '') return cur;
  }
  return '';
};

export function AdDataDrawer({ isOpen, onClose, companyName, source, data }: AdDataDrawerProps) {
  if (!isOpen) return null;
  const items = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
  const facebookPage = data?.facebook_page;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 w-[700px] max-w-[95vw] h-full bg-gray-900 border-l border-gray-700 z-50 overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h3 className="text-xl font-bold">{companyName} - {source.toUpperCase()} Ads</h3>
              <p className="text-sm text-gray-400">Showing {items.length} ads</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">
              <X className="w-6 h-6" />
            </button>
          </div>

          {facebookPage && (
            <details className="border border-gray-700 rounded bg-gray-800">
              <summary className="p-3 cursor-pointer text-sm text-gray-300 hover:bg-gray-700">
                View Facebook Page Data
              </summary>
              <div className="border-t border-gray-700 p-3 bg-gray-900">
                <pre className="text-xs text-gray-400 overflow-auto max-h-64">
                  {JSON.stringify(facebookPage, null, 2)}
                </pre>
              </div>
            </details>
          )}

          {items.length === 0 && (
            <div className="text-gray-400 text-sm">No ad data found.</div>
          )}

          <div className="space-y-4">
            {items.map((item: any, idx: number) => {
              const image =
                pick(item, ['imageUrl', 'image', 'thumbnailUrl', 'thumbnail', 'picture']) ||
                pickNested(item, ['creative.imageUrl', 'creative.image', 'adCreative.imageUrl']);
              const text =
                pick(item, ['text', 'body', 'description', 'adText', 'message', 'title']) ||
                pickNested(item, ['creative.text', 'creative.body', 'adCreative.text']);
              const advertiser =
                pick(item, ['advertiser', 'advertiserName', 'pageName', 'accountName', 'companyName']) ||
                pickNested(item, ['page.name', 'account.name']);
              const date =
                pick(item, ['date', 'startDate', 'startTime', 'createdAt', 'publishDate']) ||
                pickNested(item, ['meta.startDate', 'meta.createdAt']);
              const url =
                pick(item, ['url', 'landingPageUrl', 'destinationUrl', 'link']) ||
                pickNested(item, ['creative.link', 'adCreative.link']);
              const keywords =
                pick(item, ['keywords', 'keyword', 'searchQuery', 'query']) ||
                pickNested(item, ['meta.keywords']);

              return (
                <div key={idx} className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                  <div className="flex gap-4">
                    {image && (
                      <img src={String(image)} alt="ad" className="w-24 h-24 object-cover rounded" />
                    )}
                    <div className="flex-1">
                      <div className="text-sm text-gray-300">
                        {advertiser && <div><span className="text-gray-500">Advertiser:</span> {esc(String(advertiser))}</div>}
                        {date && <div><span className="text-gray-500">Date:</span> {esc(String(date))}</div>}
                        {keywords && <div><span className="text-gray-500">Keywords:</span> {esc(String(keywords))}</div>}
                      </div>
                      {text && (
                        <div className="mt-2 text-sm text-gray-200 whitespace-pre-line">
                          {esc(String(text)).slice(0, 600)}
                        </div>
                      )}
                      {url && (
                        <div className="mt-2 text-xs text-blue-400">
                          <a href={String(url)} target="_blank" rel="noreferrer" className="hover:underline">
                            {String(url)}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  <details className="mt-3 text-xs text-gray-400">
                    <summary className="cursor-pointer">View raw data</summary>
                    <pre className="mt-2 p-2 bg-gray-900 border border-gray-700 rounded max-h-64 overflow-auto">
                      {JSON.stringify(item, null, 2)}
                    </pre>
                  </details>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
